/* eslint-disable @altermaind/you-shall-not-pass */
import { FC, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography } from '@altermaind/web/ui';
import { AlertBadge, Badge, RenderLocalesWithComponents } from '@/_shared/components';
import { Routes } from '@/_shared/constants';
import { useNotification } from '@/_shared/hooks';
import { getEmployeesStatsCall, getEmployeesCall } from '@/people/state/api';
import { AccountStatus } from '@/swagger/constants/anagraphics-enums';
import { Steps } from '../../state';
import {
  getSurveyCampaignInvitationByTemplateIdCall,
  getSurveysCampaignsCall,
  getTemplatesCall,
  postSurveysCampaignsCall,
  putSurveyCampaignByIdCall,
} from '../../state/api';

/**
 * @param previousStep - Step to navigate back to when "Indietro" is clicked.
 *   Provided by SendSurvey as `recipientsBackStep` (the step active when the user
 *   last navigated to STEP_RECIPIENTS). Defaults to STEP_CHOOSER.
 * @param inviteAll - When true, the send API receives no userIds (backend invites everyone).
 *   Also controls the edit-icon behavior: clicking edit pre-selects all departments and
 *   navigates to STEP_DEPARTMENTS instead of using `previousStep`.
 * @param setSelectedDepartments - Required when `inviteAll` is true so the edit icon can
 *   pre-populate all department names before navigating back to STEP_DEPARTMENTS.
 */
type Props = {
  totalToInvite?: number;
  setStep: (step: Steps) => void;
  previousStep?: Steps;
  selectedUsersIds?: string[];
  currentTemplateId?: string;
  currentVersionId?: number;
  currentCampaignId?: string;
  isExistingCampaign?: boolean;
  setIsCompleted: (completed: boolean) => void;
  isVisible?: boolean;
  inviteAll?: boolean;
  selectedTemplate?: string;
  setSelectedDepartments?: (depts: string[]) => void;
};

export const StepRecapAll: FC<Props> = ({
  totalToInvite,
  selectedUsersIds,
  setStep,
  previousStep = Steps.STEP_CHOOSER,
  currentTemplateId,
  currentVersionId,
  currentCampaignId,
  isExistingCampaign = false,
  setIsCompleted,
  isVisible,
  inviteAll = false,
  selectedTemplate,
  setSelectedDepartments,
}) => {
  const t = useTranslations('surveys.sendSurvey.stepRecapAll');
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { response: responseGetEmployeesStats } = getEmployeesStatsCall.hook();

  const tShared = useTranslations();
  const { notify } = useNotification();

  const { execute: executePostSurveysCampaigns, loading: loadingPostSurveysCampaigns } =
    postSurveysCampaignsCall.hook();

  const { execute: executePutSurveyCampaignById, loading: loadingPutSurveyCampaignById } =
    putSurveyCampaignByIdCall.hook();

  const { execute: executeGetEmployees, fullRequest: getEmployeesFullRequest } =
    getEmployeesCall.hook();

  const { response: responseGetSurveysCampaigns } = getSurveysCampaignsCall.hook();
  const { response: responseGetTemplates } = getTemplatesCall.hook();
  const { departments } = getSurveyCampaignInvitationByTemplateIdCall.hook();

  const resolveDeptName = (name: string | undefined) =>
    name?.toLowerCase() === 'unknown' ? t('unknownDepartment') : (name ?? '');

  const selectedDepartmentNames = inviteAll
    ? departments?.map(dept => resolveDeptName(dept.name)).filter(Boolean)
    : departments
        ?.filter(dept => dept.employees?.some(emp => selectedUsersIds?.includes(emp.email ?? '')))
        .map(dept => resolveDeptName(dept.name))
        .filter(Boolean);

  const allDepartmentEmployees = departments?.flatMap(dept => dept.employees ?? []) ?? [];
  const totalNewInvited = inviteAll
    ? allDepartmentEmployees.filter(emp => emp.status === AccountStatus.NOT_INVITED).length
    : allDepartmentEmployees
        .filter(emp => selectedUsersIds?.includes(emp.email ?? ''))
        .filter(emp => emp.status === AccountStatus.NOT_INVITED).length;

  const onConfirmSend = useCallback(() => {
    const internalSurveyId = selectedTemplate
      ? responseGetTemplates?.find(template => template.name === selectedTemplate)?.id
      : surveyId;

    if (internalSurveyId) {
      const versions = responseGetTemplates?.find(
        template => template.id === internalSurveyId,
      )?.versions;
      const lastTemplateVersion = versions ? versions[versions.length - 1] : undefined;

      const internalExistingCampaign = selectedTemplate
        ? responseGetSurveysCampaigns?.findLast(
            campaign => campaign.surveyTemplateId === internalSurveyId,
          )?.id
        : isExistingCampaign;

      const internalCurrentCampaignId = selectedTemplate
        ? responseGetSurveysCampaigns?.findLast(
            campaign => campaign.surveyTemplateId === internalSurveyId,
          )?.id
        : currentCampaignId;

      if (!internalExistingCampaign) {
        executePostSurveysCampaigns({
          surveyTemplateId: currentTemplateId || internalSurveyId,
          templateVersion: currentVersionId || lastTemplateVersion?.version,
          invitedUserIds: inviteAll ? undefined : selectedUsersIds,
          inviteAll,
          isVisible: false,
          successSaga: () => {
            // After successfully creating the campaign, we fetch the employees again to update the stats in the UI.
            executeGetEmployees(getEmployeesFullRequest.payload);
            setIsCompleted(true);
            setStep(Steps.STEP_CONFIRM);
          },
          errorSaga: () => {
            notify(tShared('errors.genericApiError'), 'error');
          },
        });
      } else {
        executePutSurveyCampaignById({
          id: internalCurrentCampaignId as string,
          surveyTemplateId: currentTemplateId || surveyId,
          templateVersion: currentVersionId || lastTemplateVersion?.version,
          invitedUserIds: inviteAll ? undefined : selectedUsersIds,
          inviteAll,
          isVisible,
          successSaga: () => {
            // After successfully creating the campaign, we fetch the employees again to update the stats in the UI.
            executeGetEmployees(getEmployeesFullRequest.payload);
            setIsCompleted(true);
            setStep(Steps.STEP_CONFIRM);
          },
          errorSaga: () => {
            notify(tShared('errors.genericApiError'), 'error');
          },
        });
      }
    }
  }, [
    currentCampaignId,
    currentTemplateId,
    currentVersionId,
    executePostSurveysCampaigns,
    executePutSurveyCampaignById,
    executeGetEmployees,
    getEmployeesFullRequest,
    inviteAll,
    isExistingCampaign,
    isVisible,
    notify,
    responseGetSurveysCampaigns,
    responseGetTemplates,
    selectedTemplate,
    selectedUsersIds,
    setIsCompleted,
    setStep,
    surveyId,
    tShared,
  ]);

  return (
    <Box mb='xs' mt='m' width='100%'>
      <Typography textAlign='center' variant='p2/light'>
        {t('description')}
      </Typography>
      <Card mt='xs' backgroundColor='grey/dark/background'>
        <Box display='flex' justifyContent='space-between' alignItems='center' flexDirection='row'>
          <Box display='flex' alignItems='center' flexDirection='row'>
            <Svg name='document' size='s' />
            <Typography ml='xxs' variant='h4'>
              {t('title', {
                name: selectedTemplate,
              })}
            </Typography>
          </Box>
          <Box>
            <Box
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (inviteAll && setSelectedDepartments) {
                  const allDeptNames = departments?.map(d => d.name ?? '').filter(Boolean) ?? [];
                  setSelectedDepartments(allDeptNames);
                  setStep(Steps.STEP_DEPARTMENTS);
                } else {
                  setStep(previousStep);
                }
              }}
            >
              <Svg name='edit' size='s' />
            </Box>
          </Box>
        </Box>
        <Box mt='s'>
          <Typography mt='xxs' variant='p2/light'>
            {t('totalsSelected')}
          </Typography>
          <Box mt='xxs' display='flex' alignItems='center' flexDirection='row'>
            <Svg name='users' size='xs' />
            <Typography ml='xxs' variant='p2/light'>
              {selectedUsersIds?.length || totalToInvite || responseGetEmployeesStats?.completed}
            </Typography>
          </Box>
        </Box>
        {selectedDepartmentNames && selectedDepartmentNames.length > 0 && (
          <Box mt='s'>
            <Typography variant='p2/light'>
              {t('selectedDepartmentsLabel', { count: selectedDepartmentNames.length })}
            </Typography>
            <Box
              mt='xs'
              display='flex'
              flexDirection='row'
              style={{ flexWrap: 'wrap', gap: '8px' }}
            >
              {selectedDepartmentNames.map(dept => (
                <Badge
                  key={dept}
                  label={dept}
                  textProps={{ variant: 'p3/light', color: 'primary/text' }}
                  py='xxxs'
                />
              ))}
            </Box>
          </Box>
        )}
      </Card>

      {totalNewInvited ? (
        <AlertBadge
          containerProps={{ mt: 'm' }}
          type='warning'
          iconName='warning'
          label={
            <RenderLocalesWithComponents
              text={t('alertBadge.label')}
              placeholders={{
                '!{TOTAL_NEW_INVITED}!': (
                  <Typography as='span' variant='p2/light' color='primary'>
                    {t('alertBadge.totalInvited', {
                      totalNewInvited,
                    })}
                  </Typography>
                ),
                '!{SUBSCRIPTION_AMOUNT_NOTICE}!': (
                  <Typography as='span' variant='p2'>
                    {t('alertBadge.subscriptionAmountNotice')}
                  </Typography>
                ),
                '!{SETTINGS_LINK}!': (
                  <Typography
                    as='span'
                    variant='p2/light'
                    color='primary'
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(Routes.Settings)}
                  >
                    {t('alertBadge.settingsLink')}
                  </Typography>
                ),
              }}
            />
          }
        />
      ) : null}

      <Box justifyContent='flex-end' display='flex' flexDirection='row'>
        <Button onClick={() => setStep(previousStep)} mt='l' mr='xs' variant='link/primary'>
          {t('buttons.back')}
        </Button>
        <Button
          onClick={onConfirmSend}
          mt='l'
          data-testid='button-next-step-2-send-survey'
          variant='primary'
          spinnerColor='white'
          loading={loadingPostSurveysCampaigns || loadingPutSurveyCampaignById}
        >
          {t('buttons.send')}
        </Button>
      </Box>
    </Box>
  );
};
